import { usePriceVisibility, useUser, useUserSettings } from "@/composables/user";
import { LoginMarkerService } from "@/services/user/auth/login/loginMarkerService";
import { migrateTokenFromIndexedDb, TokenService } from "@/services/user/auth/login/tokenService";
import { fetchUserSettings } from "@/services/user/settings";
import { type User } from "@/services/user/userTypes";
import * as PostHog from "@/services/analytics/postHog";
import { Authentication, type AuthenticationPromise } from "@/services/user/auth/login/authenticationService";
import { fetchUserBankInfo } from "@/services/user/bankInfo";
import { fetchUserAddress } from "@/services/user/profile";
import { useAppStore } from "@/composables";
import { afterLogout } from "@/services/user/auth/login/afterLogoutService";
import { FallbackCurrencyId } from "@/services/currencies/currenciesTypes";

export async function checkUserOnInitialPageLoad(): Promise<AuthenticationPromise> {
  if (Authentication.isInProgressOrDone()) {
    return Authentication.promise;
  } else {
    await migrateTokenFromIndexedDb();
    const token = TokenService.get();
    if (token) {
      return Authentication.run({ token });
    } else {
      return null;
    }
  }
}

export async function login(email: string, password: string): Promise<AuthenticationPromise> {
  Authentication.reset();
  return Authentication.run({ email, password });
}

export async function logout(invalidateInApi = true): Promise<void> {
  await afterLogout(invalidateInApi);
  Authentication.reset();
}

export async function setupUserInApp(user: User): Promise<void> {
  TokenService.store(user.token);
  LoginMarkerService.markLogin();

  PostHog.identifyUser(user);

  const userStore = useUser();
  userStore.user.value = user;

  usePriceVisibility().arePricesVisible.value = user.showPrices;

  const userSettingsStore = useUserSettings();

  await Promise.all([
    fetchUserSettings().then((settings) => (userSettingsStore.userSettings.value = settings)), //
    fetchUserBankInfo().then((bankInfo) => (userSettingsStore.userBankInfo.value = bankInfo)),
    fetchUserAddress().then((address) => (userSettingsStore.userAddress.value = address)),
  ]);

  const appStore = useAppStore();
  const userPreferredCurrencyId = userSettingsStore.userSettings.value?.displayCurrencyId ?? FallbackCurrencyId;
  appStore.setCurrencyId(userPreferredCurrencyId);
}
